import * as THREE from 'three';

/** A self-contained 3D illustration mounted inside a portal card. */
export interface PortalScene {
  readonly root: THREE.Group;
  /** @param elapsed seconds since mount @param hover eased 0..1 hover amount */
  update(elapsed: number, hover: number): void;
}

export type SceneFactory = (accent: THREE.Color) => PortalScene;

export function bodyMaterial(color: THREE.ColorRepresentation = 0x4a579b): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color,
    metalness: 0.62,
    roughness: 0.26,
    clearcoat: 0.7,
    clearcoatRoughness: 0.22,
  });
}

export function accentMaterial(accent: THREE.Color, emissiveIntensity = 0.5): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: accent,
    metalness: 0.3,
    roughness: 0.16,
    clearcoat: 1,
    clearcoatRoughness: 0.1,
    emissive: accent,
    emissiveIntensity,
  });
}

export function glowMaterial(accent: THREE.Color, opacity = 0.9): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({ color: accent, transparent: true, opacity });
}

export function glassMaterial(accent: THREE.Color): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: 0xeaf0ff,
    metalness: 0.1,
    roughness: 0.06,
    transmission: 0.68,
    thickness: 0.55,
    transparent: true,
    opacity: 0.86,
    clearcoat: 1,
    emissive: accent,
    emissiveIntensity: 0.09,
  });
}

/** Shared glowing disc every scene sits on, so the four cards feel like a set. */
export function createPedestal(accent: THREE.Color): THREE.Group {
  const group = new THREE.Group();

  const disc = new THREE.Mesh(
    new THREE.CylinderGeometry(1.72, 1.92, 0.14, 72),
    new THREE.MeshPhysicalMaterial({
      color: 0xdbe3f7,
      metalness: 0.25,
      roughness: 0.45,
      clearcoat: 0.9,
    }),
  );

  const ring = new THREE.Mesh(new THREE.TorusGeometry(1.76, 0.022, 16, 120), glowMaterial(accent, 0.95));
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.085;

  const halo = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.012, 12, 96), glowMaterial(accent, 0.35));
  halo.rotation.x = Math.PI / 2;
  halo.position.y = 0.09;

  group.add(disc, ring, halo);
  group.position.y = -1.5;
  return group;
}

/** Evenly spaced motes that drift around a scene. */
export function createMotes(accent: THREE.Color, count = 14, radius = 2.1): THREE.Points {
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const r = radius * (0.65 + Math.random() * 0.5);
    positions[i * 3] = Math.cos(angle) * r;
    positions[i * 3 + 1] = -1.1 + Math.random() * 2.8;
    positions[i * 3 + 2] = Math.sin(angle) * r;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      color: accent,
      size: 0.075,
      transparent: true,
      opacity: 0.7,
      sizeAttenuation: true,
      depthWrite: false,
    }),
  );
}

/** Flat-toothed gear profile usable with ExtrudeGeometry. */
export function gearShape(outer: number, innerHole: number, teeth: number, toothDepth: number): THREE.Shape {
  const shape = new THREE.Shape();
  const steps = teeth * 2;
  const step = (Math.PI * 2) / steps;

  for (let i = 0; i < steps; i++) {
    const radius = i % 2 === 0 ? outer : outer - toothDepth;
    const angle = i * step;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    if (i === 0) {
      shape.moveTo(x, y);
    } else {
      shape.lineTo(x, y);
    }
  }
  shape.closePath();

  const hole = new THREE.Path();
  hole.absarc(0, 0, innerHole, 0, Math.PI * 2, true);
  shape.holes.push(hole);
  return shape;
}

export function extrude(shape: THREE.Shape, depth: number, bevel = 0.045): THREE.ExtrudeGeometry {
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 4,
    curveSegments: 32,
  });
  geometry.center();
  return geometry;
}

export function disposeObject3D(root: THREE.Object3D): void {
  root.traverse((object) => {
    const withGeometry = object as Partial<THREE.Mesh>;
    withGeometry.geometry?.dispose();

    const material = withGeometry.material;
    if (Array.isArray(material)) {
      material.forEach((entry) => entry.dispose());
    } else {
      material?.dispose();
    }
  });
}
